// auth.ts
import { defineStore } from 'pinia';
import { useLocalStorage } from '@vueuse/core';
import AUTH_LOCAL_STORAGES from '@/store/auth/enums/AuthLocalStorages';
import useToken from '@/composables/useToken/useToken';
import User from '@/store/auth/types/User';
import Auth from '@/store/auth/types/Auth';
import identityService from '@/services/identityService';
const { decodeToken, isExpired } = useToken();

// Default values for the store state
const storeDefaults = {
  token: '' as string | null,
  refreshToken: '' as string | null,
  user: {} as User | null,
  refreshTokenTimeout: '' as string | null,
};

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: useLocalStorage(AUTH_LOCAL_STORAGES.user, storeDefaults.user),
    token: useLocalStorage(AUTH_LOCAL_STORAGES.token, storeDefaults.token),
    refreshToken: useLocalStorage(AUTH_LOCAL_STORAGES.refreshToken, storeDefaults.refreshToken),
    refreshTokenTimeout: useLocalStorage(
      AUTH_LOCAL_STORAGES.refreshTokenTimeout,
      storeDefaults.refreshTokenTimeout
    ),
  }),

  actions: {
    // Set or remove user and token info
    setUser(user: User) {
      this.user = user;
    },
    removeUser() {
      this.user = null;
    },
    setAuth(auth: Auth) {
      this.token = auth.token;
      this.refreshToken = auth.refreshToken;
    },
    setToken(token: string) {
      this.token = token;
    },
    removeAuth() {
      this.token = null;
      this.refreshToken = null;
    },
    setRefreshTokenTimeout(timeout: string) {
      this.refreshTokenTimeout = timeout;
    },
    removeRefreshTokenTimeout() {
      this.refreshTokenTimeout = null;
    },

    /**
     * Schedules a token refresh before the current token expires.
     * The refresh is scheduled 1 minute before token expiration (if possible).
     */
    scheduleTokenRefresh(expiration: number) {
      const now = Date.now();
      const expiresIn = expiration * 1000 - now; // Time in ms until token expires
      const refreshTime = expiresIn > 60000 ? expiresIn - 60000 : 0;
      const timer = setTimeout(() => {
        this.tokenRefresh();
      }, refreshTime);
      this.setRefreshTokenTimeout(timer.toString());
    },

    /**
     * Refreshes the token using the stored refresh token.
     * On success, updates the token and re-schedules the next refresh.
     */
    tokenRefresh() {
      if (!this.refreshToken) {
        this.logOutUser();
        return;
      }
      const fetch = identityService.tokenRefresh(this.refreshToken);
      fetch.onFetchResponse((response: any) => {
        response.json().then((x: { data: { jwtToken: string } }) => {
          this.setToken(x.data.jwtToken);
          const newDecodedToken = decodeToken(x.data.jwtToken);
          this.scheduleTokenRefresh(newDecodedToken.exp);
        });
      });
      fetch.onFetchError(() => {
        this.logOutUser();
      });
    },

    /**
     * Initializes authentication from tokens stored in persistent storage.
     * This should be called on app load to re-establish the user session.
     */
    initializeAuthFromStorage() {
      const token = this.token;
      const refreshToken = this.refreshToken;
      if (token && refreshToken) {
        const decodedToken = decodeToken(token);
        if (decodedToken.exp > Date.now() / 1000) {
          this.setUser({
            id: decodedToken.nameIdentifier,
            username: decodedToken.name,
            email: decodedToken.email || '',
            role: decodedToken.role,
          });
          this.setAuth({ token, refreshToken });
          this.scheduleTokenRefresh(decodedToken.exp);
        } else {
          this.logOutUser();
        }
      } else {
        this.logOutUser();
      }
    },
    /**
     * Logs out the user by clearing tokens, user data,
     * and canceling any scheduled token refresh.
     */
    logOutUser() {
      if (this.refreshTokenTimeout) {
        clearTimeout(parseInt(this.refreshTokenTimeout));
        this.removeRefreshTokenTimeout();
      }
      this.removeUser();
      this.removeAuth();
      localStorage.removeItem(AUTH_LOCAL_STORAGES.token);
      localStorage.removeItem(AUTH_LOCAL_STORAGES.refreshToken);
      // Optionally, revoke tokens on the server:
      if (this.refreshToken) {
        const fetch = identityService.logOutUser(this.refreshToken);
        fetch.onFetchResponse(() => { });
        // fetch.onFetchError((error: any) => {});
      }
      if (this.token) {
        const fetch = identityService.logOutUser(this.token);
        fetch.onFetchResponse(() => { });
        // fetch.onFetchError((error: any) => {});
      }
    },

    // Returns true if the user is considered authorized.
    isAuthorized() {
      return !!this.token && !!this.refreshToken;
    },

    // Returns true if the JWT token is expired.
    isTokenExpired() {
      return this.token ? isExpired(this.token) : true;
    },

    // Returns true if the refresh token is expired.
    isRefreshTokenExpired() {
      return this.refreshToken ? isExpired(this.refreshToken) : true;
    },
  },

  getters: {
    getToken(state): string | null {
      return state.token;
    },
    getUser(state): User | null {
      return state.user;
    },
    getRefreshToken(state): string | null {
      return state.refreshToken;
    },
    getRefreshTokenTimeout(state): string | null {
      return state.refreshTokenTimeout;
    },
  },
});
